import { useRef, useEffect, useState } from 'react';

interface MuxPlayerProps {
  /** The Mux playback ID */
  playbackId: string;
  /** Signed token for protected playback */
  signedToken?: string;
  /** Optional poster/thumbnail image */
  poster?: string;
  /** Additional CSS classes */
  className?: string;
  /** Whether to autoplay (muted required for autoplay) */
  autoplay?: boolean;
  /** Whether to loop the video */
  loop?: boolean;
  /** Whether to mute the video */
  muted?: boolean;
  /** Callback when video starts playing */
  onPlay?: () => void;
  /** Callback when video ends */
  onEnded?: () => void;
  /** Callback for progress updates (at 25%, 50%, 75% milestones) */
  onProgress?: (progress: { currentTime: number; duration: number; percent: number }) => void;
  /** Max buffer length in seconds (default: 30) - controls bandwidth usage */
  maxBufferLength?: number;
  /** Max buffer size in MB (default: 30) */
  maxBufferSizeMB?: number;
}

/**
 * Hook to detect potential screen capture/recording
 */
function useScreenCaptureDetection() {
  const [isCapturing, setIsCapturing] = useState(false);
  const [isHidden, setIsHidden] = useState(false);

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsHidden(document.hidden);
    };

    const originalGetDisplayMedia = navigator.mediaDevices?.getDisplayMedia;
    if (navigator.mediaDevices && originalGetDisplayMedia) {
      navigator.mediaDevices.getDisplayMedia = async function(constraints) {
        setIsCapturing(true);
        return originalGetDisplayMedia.call(navigator.mediaDevices, constraints);
      };
    }

    const handlePiP = () => {
      if (document.pictureInPictureElement) {
        setIsCapturing(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('enterpictureinpicture', handlePiP);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('enterpictureinpicture', handlePiP);
      
      if (navigator.mediaDevices && originalGetDisplayMedia) {
        navigator.mediaDevices.getDisplayMedia = originalGetDisplayMedia;
      }
    };
  }, []);

  return { isCapturing, isHidden };
}

/**
 * Mux Video Player component with DRM support.
 * 
 * Mux provides:
 * - Widevine DRM (Chrome, Firefox, Edge, Android)
 * - FairPlay DRM (Safari, iOS)
 * - Hardware-level video encryption that prevents screen recording
 */
export function MuxPlayer({
  playbackId,
  signedToken,
  poster,
  className = '',
  autoplay = false,
  loop = false,
  muted = false,
  onPlay,
  onEnded,
  onProgress,
  maxBufferLength = Number(import.meta.env.VITE_VIDEO_MAX_BUFFER_LENGTH) || 30,
  maxBufferSizeMB = Number(import.meta.env.VITE_VIDEO_MAX_BUFFER_SIZE_MB) || 30,
}: MuxPlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hasPlayedRef = useRef(false);
  const lastProgressMilestoneRef = useRef(0);
  
  const { isCapturing, isHidden } = useScreenCaptureDetection();
  const showWarning = isCapturing || isHidden;

  // Build the HLS stream URL
  const streamUrl = signedToken
    ? `https://stream.mux.com/${playbackId}.m3u8?token=${signedToken}`
    : `https://stream.mux.com/${playbackId}.m3u8`;

  // Build poster URL
  const posterUrl = poster || (signedToken
    ? `https://image.mux.com/${playbackId}/thumbnail.jpg?token=${signedToken}`
    : `https://image.mux.com/${playbackId}/thumbnail.jpg`);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handlePlay = () => {
      if (!hasPlayedRef.current) {
        hasPlayedRef.current = true;
        onPlay?.();
      }
    };

    const handleEnded = () => {
      onEnded?.();
    };

    const handleTimeUpdate = () => {
      if (!onProgress || video.duration === 0) return;
      
      const percent = Math.round((video.currentTime / video.duration) * 100);
      const milestones = [25, 50, 75];
      
      for (const milestone of milestones) {
        if (percent >= milestone && lastProgressMilestoneRef.current < milestone) {
          lastProgressMilestoneRef.current = milestone;
          onProgress({
            currentTime: video.currentTime,
            duration: video.duration,
            percent: milestone,
          });
        }
      }
    };

    video.addEventListener('play', handlePlay);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('timeupdate', handleTimeUpdate);

    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [onPlay, onEnded, onProgress]);

  // Reset refs when playbackId changes
  useEffect(() => {
    hasPlayedRef.current = false;
    lastProgressMilestoneRef.current = 0;
  }, [playbackId]);

  // Prevent right-click
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      return false;
    };

    container.addEventListener('contextmenu', handleContextMenu);
    return () => container.removeEventListener('contextmenu', handleContextMenu);
  }, []);

  // Load HLS.js for browsers that don't support native HLS
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Safari supports HLS natively
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = streamUrl;
      return;
    }

    // Load HLS.js dynamically for other browsers
    const loadHls = async () => {
      try {
        // @ts-ignore
        if (typeof Hls === 'undefined') {
          const script = document.createElement('script');
          script.src = 'https://cdn.jsdelivr.net/npm/hls.js@1';
          script.async = true;
          document.head.appendChild(script);
          
          await new Promise((resolve) => {
            script.onload = resolve;
          });
        }

        // @ts-ignore
        if (Hls.isSupported()) {
          // @ts-ignore
          const hls = new Hls({
            enableWorker: true,
            emeEnabled: true,
            // Buffer control for bandwidth optimization
            maxBufferLength: maxBufferLength,           // Max buffer ahead (seconds)
            maxMaxBufferLength: maxBufferLength * 2,    // Absolute max buffer (seconds)
            maxBufferSize: maxBufferSizeMB * 1000000,   // Max buffer size (bytes)
            maxBufferHole: 0.5,                         // Max gap in buffer (seconds)
            // ABR (Adaptive Bitrate) settings
            abrEwmaDefaultEstimate: 500000,             // Initial bandwidth estimate (500kbps)
            startLevel: -1,                             // Auto-select quality
          });
          
          hls.loadSource(streamUrl);
          hls.attachMedia(video);
        }
      } catch (error) {
        console.error('Failed to load HLS.js:', error);
      }
    };

    loadHls();
  }, [streamUrl, maxBufferLength, maxBufferSizeMB]);

  return (
    <div 
      ref={containerRef}
      className={`relative ${className}`}
      data-protected
      style={{ WebkitTouchCallout: 'none' }}
    >
      <video
        ref={videoRef}
        poster={posterUrl}
        controls
        controlsList="nodownload noplaybackrate"
        disablePictureInPicture
        playsInline
        autoPlay={autoplay}
        loop={loop}
        muted={muted}
        style={{
          width: '100%',
          height: '100%',
          filter: showWarning ? 'blur(20px)' : 'none',
          transition: 'filter 0.3s ease',
        }}
      />
      
      {/* Screen capture warning overlay */}
      {showWarning && (
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
            color: 'white',
            textAlign: 'center',
            padding: '20px',
          }}
        >
          <svg 
            width="64" 
            height="64" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2"
            style={{ marginBottom: '16px', color: '#ef4444' }}
          >
            <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '8px' }}>
            {isCapturing ? 'Screen Recording Detected' : 'Video Paused'}
          </h3>
          <p style={{ fontSize: '0.875rem', opacity: 0.8, maxWidth: '300px' }}>
            {isCapturing 
              ? 'Screen recording or sharing is not allowed for this content.'
              : 'Please return to this tab to continue watching.'
            }
          </p>
        </div>
      )}
    </div>
  );
}

